/**
 * MonthIskFlow — "¿cómo viene mi mes (en ISK)?".
 *
 * Tile del Dashboard que agrega el flujo de ISK del active char en los
 * últimos N días (default 30, cap 60). Combina:
 *   - Journal income (market sells + bounties + mission rewards + ...)
 *   - Journal expense (taxes, broker fees, market buys)
 *   - Mining ledger valuado a Jita buy
 *
 * = `net = income + mining - expense` (mining es +, expenses son -).
 *
 * Patrón visual:
 *   - Hero: total_net del período (número grande). El industrialist mira
 *     este número apenas abre el dashboard.
 *   - 3 tiles breakdown: Income / Mining / Expense (sign-aware, color).
 *   - Strip de N mini-bars (1 por día, ordenadas cronológicamente).
 *     Altura = |net| normalizada al máximo del período. Color por signo
 *     (success/danger). Tooltip con title= para ver income/mining/expense
 *     del día sin abrir ningún popover (mantiene el componente "puro CSS",
 *     sin estado ni portals).
 *   - Best day chip + meta footer (días activos, journal pages walked).
 *
 * Caveats:
 *   - mining se valúa a Jita buy (sell avg de ESI history), ~3-10% optimista.
 *   - El backend limita el walk a 10 páginas de journal (~2.5K entradas).
 *     Si el char tiene >2.5K transacciones en N días, la respuesta tiene
 *     un `note` warn. Lo mostramos al pie.
 *   - "Días activos" = días con income || mining || expense > 0. Días
 *     forward-filleados con 0 no cuentan.
 *
 * Failure semantics:
 *   - isError → banner rojo. El dashboard sigue funcionando.
 *   - Skeleton mientras carga (default 0 stale → refresca cada focus).
 */

import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'

interface DayRow {
  date: string
  income: number
  expense: number
  mining: number
  net: number
  top_income_ref_type: { ref_type: string; total: number } | null
}

interface MonthData {
  days: number
  start_day: string
  end_day: string
  summary: {
    total_income: number
    total_mining: number
    total_net: number
    days_active: number
    best_day: { date: string; net: number } | null
    journal_pages_walked: number
  }
  by_day: DayRow[]
  cached: boolean
  note?: string
}

export function MonthIskFlow({ days = 30 }: { days?: number }) {
  const { data, isLoading, isError, error, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard-month', days],
    queryFn: () => api.dashboardMonth(days),
    // 5min stale = matches server KV TTL. Refresca on focus para "volví al tab".
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  })

  if (isError) {
    return (
      <div className="bg-eve-danger/10 border border-eve-danger/30 rounded-md p-3 text-eve-danger text-sm">
        Error cargando el mes: {(error as Error).message}
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="bg-eve-panel border border-eve-border rounded-md p-5">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="h-3 w-1/3 bg-eve-border/40 rounded animate-pulse" />
            <div className="h-10 w-2/3 bg-eve-border/30 rounded animate-pulse mt-2" />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="h-20 bg-eve-border/30 rounded animate-pulse" />
          <div className="h-20 bg-eve-border/30 rounded animate-pulse" />
          <div className="h-20 bg-eve-border/30 rounded animate-pulse" />
        </div>
        <div className="mt-4 h-12 bg-eve-border/30 rounded animate-pulse" />
      </div>
    )
  }

  const hasActivity =
    data.summary.total_income > 0 ||
    data.summary.total_mining > 0 ||
    data.by_day.some((d) => d.expense > 0)

  return (
    <div
      className={`bg-gradient-to-br from-eve-panel to-eve-bg border rounded-md p-5 ${
        hasActivity ? 'border-eve-accent/30 shadow-gold' : 'border-eve-border'
      }`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-eve-muted text-[10px] uppercase tracking-widest font-medium">
            Últimos {data.days} días · {data.start_day} → {data.end_day}
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <span
              className={`font-display text-3xl sm:text-4xl font-bold ${
                data.summary.total_net >= 0 ? 'text-eve-success' : 'text-eve-danger'
              }`}
            >
              {data.summary.total_net >= 0 ? '+' : '−'}
              {formatISKShort(Math.abs(data.summary.total_net))}
            </span>
            <span className="text-base font-mono text-eve-muted">ISK net</span>
          </div>
          <div className="text-xs text-eve-muted mt-1 italic">
            {hasActivity
              ? 'income + mining − expense · todas las wallets del char'
              : 'Sin actividad registrada en el período'}
          </div>
        </div>
        <div className="text-[10px] text-eve-muted-2 font-mono italic text-right">
          {data.cached ? 'cached · ' : ''}
          ref {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '—'} UTC
        </div>
      </div>

      {/* Breakdown por fuente */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SourceTile
          label="Income"
          tone="success"
          value={data.summary.total_income}
          hint={
            data.summary.total_income > 0
              ? `${formatISK(data.summary.total_income, 0)} ISK brutos`
              : 'sin entradas positivas'
          }
        />
        <SourceTile
          label="Mining"
          tone="accent"
          value={data.summary.total_mining}
          hint={
            data.summary.total_mining > 0
              ? `${formatISK(data.summary.total_mining, 0)} ISK (Jita buy)`
              : 'sin actividad minera'
          }
        />
        <SourceTile
          label="Expense"
          tone="danger"
          value={data.by_day.reduce((acc, d) => acc + d.expense, 0)}
          hint="tax + fees + market buys"
        />
      </div>

      {/* Strip de días */}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-wider text-eve-muted mb-1.5">
          Contribución diaria
        </div>
        <DayStrip days={data.by_day} />
      </div>

      {/* Footer: best day chip + meta */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
        {data.summary.best_day && (
          <div className="inline-flex items-center gap-2 bg-eve-success/10 border border-eve-success/30 text-eve-success rounded px-2.5 py-1">
            <span className="text-[10px] uppercase tracking-wider">Best day</span>
            <span className="font-mono font-semibold">
              {data.summary.best_day.date}
            </span>
            <span className="font-mono">
              +{formatISKShort(data.summary.best_day.net)} ISK
            </span>
          </div>
        )}
        <div className="text-eve-muted">
          {data.summary.days_active}{' '}
          {data.summary.days_active === 1 ? 'día activo' : 'días activos'}
        </div>
        <div className="text-eve-muted-2 text-[10px]">
          · {data.summary.journal_pages_walked} journal pages walked
        </div>
      </div>

      {data.note && (
        <div className="mt-3 text-xs text-eve-warn italic border-t border-eve-border/40 pt-2">
          {data.note}
        </div>
      )}
    </div>
  )
}

/**
 * DayStrip — visualiza by_day como una hilera de barras verticales.
 *
 * - Cada barra = 1 día, ancho `flex: 1` para que se adapte a N.
 * - Altura = `|net| / maxAbsNet` del período, mínimo 2px para que los
 *   días negativos también se vean.
 * - Color: success (verde) si net >= 0, danger (rojo) si < 0. Si net === 0,
 *   border-eve-border (gris neutro) — "ese día no hubo flujo".
 * - title attribute = tooltip nativo del browser con la data del día.
 *   Cero JS, cero portals, accesible.
 */
function DayStrip({ days }: { days: DayRow[] }) {
  if (days.length === 0) return null

  const maxAbs = Math.max(1, ...days.map((d) => Math.abs(d.net)))

  return (
    <div className="flex items-end gap-[2px] h-14 w-full">
      {days.map((d) => {
        const heightPct = (Math.abs(d.net) / maxAbs) * 100
        const heightPx = Math.max(2, (heightPct / 100) * 56) // 56px = h-14
        const color =
          d.net > 0
            ? 'bg-eve-success/70 hover:bg-eve-success'
            : d.net < 0
            ? 'bg-eve-danger/70 hover:bg-eve-danger'
            : 'bg-eve-border'
        const tooltip = [
          d.date,
          `net ${d.net >= 0 ? '+' : ''}${formatISKShort(d.net)} ISK`,
          `income ${formatISKShort(d.income)}`,
          `mining ${formatISKShort(d.mining)}`,
          d.expense > 0 ? `expense −${formatISKShort(d.expense)}` : null,
          d.top_income_ref_type
            ? `top: ${d.top_income_ref_type.ref_type} (${formatISKShort(d.top_income_ref_type.total)})`
            : null,
        ]
          .filter(Boolean)
          .join('\n')
        return (
          <div
            key={d.date}
            title={tooltip}
            style={{ height: `${heightPx}px` }}
            className={`flex-1 min-w-[2px] max-w-[20px] rounded-sm transition-colors cursor-default ${color}`}
          />
        )
      })}
    </div>
  )
}

function SourceTile({
  label,
  tone,
  value,
  hint,
}: {
  label: string
  tone: 'success' | 'accent' | 'danger'
  value: number
  hint: string
}) {
  const valueColor =
    tone === 'success' || tone === 'accent' ? 'text-eve-text' : 'text-eve-text'
  const dimColor =
    value > 0
      ? tone === 'success'
        ? 'text-eve-success'
        : tone === 'accent'
        ? 'text-eve-accent'
        : 'text-eve-danger'
      : 'text-eve-muted-2'
  return (
    <div className="bg-eve-bg/40 border border-eve-border/60 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-eve-muted">
        {label}
      </div>
      <div className={`font-mono text-lg font-semibold mt-0.5 ${dimColor}`}>
        {value > 0 ? '+' : value < 0 ? '−' : ''}
        {formatISKShort(Math.abs(value))}
        <span className="text-[10px] text-eve-muted ml-1.5">ISK</span>
      </div>
      <div className="text-[10px] text-eve-muted mt-0.5">{hint}</div>
    </div>
  )
}
